import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, AlertCircle, Loader2, ExternalLink, FileSpreadsheet, Cloud } from 'lucide-react';

interface OneDriveFile {
  id: string;
  name: string;
  size?: number;
  lastModifiedDateTime?: string;
  webUrl?: string;
}

export const OneDriveExcelIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [files, setFiles] = useState<OneDriveFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingExcel, setIsCreatingExcel] = useState(false);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('microsoft_tokens')
        .select('*')
        .eq('user_id', user.id)
        .single();

      setIsConnected(!!data);
    } catch (error) {
      console.error('Error checking OneDrive connection:', error);
    }
  };

  const handleOAuthConnect = async () => {
    setIsConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call the enhanced OAuth integration for Microsoft
      const { data, error } = await supabase.functions.invoke('enhanced-oauth-integration', {
        body: { action: 'initialize_oauth', provider: 'microsoft' },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to initialize OAuth');
      }

      if (data?.authUrl) {
        // Redirect to OAuth authorization URL
        window.location.href = data.authUrl;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error: any) {
      console.error('OneDrive OAuth error:', error);
      toast.error(error.message || 'Failed to start OAuth flow');
      setIsConnecting(false);
    }
  };

  const loadFiles = async () => {
    if (!isConnected) return;

    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase.functions.invoke('enhanced-oauth-integration', {
        body: { 
          action: 'list_files',
          provider: 'microsoft',
          fileType: 'excel'
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      setFiles(data?.files || []);
      toast.success(`Loaded ${data?.files?.length || 0} Excel files from OneDrive`);
    } catch (error: any) {
      console.error('Error loading files:', error);
      toast.error('Failed to load files from OneDrive');
    } finally {
      setIsLoading(false);
    }
  };

  const createExcelFile = async () => {
    setIsCreatingExcel(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      toast.info('Creating Excel file in OneDrive...');

      // Create an Excel file with sample data and upload to OneDrive
      const { data, error } = await supabase.functions.invoke('enhanced-oauth-integration', {
        body: { 
          action: 'create_spreadsheet',
          provider: 'microsoft',
          fileName: `NextWave_Analytics_${new Date().toISOString().split('T')[0]}.xlsx`,
          data: [
            ['Date', 'Metric', 'Value', 'Category', 'Target', 'Performance'],
            [new Date().toISOString().split('T')[0], 'Revenue', 10000, 'Sales', 12000, '83%'],
            [new Date().toISOString().split('T')[0], 'Leads', 250, 'Marketing', 300, '83%'],
            [new Date().toISOString().split('T')[0], 'Conversion Rate', 2.5, 'Analytics', 3.0, '83%'],
            [new Date().toISOString().split('T')[0], 'Customer Satisfaction', 4.2, 'Service', 4.5, '93%']
          ]
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success('Excel file created and uploaded to OneDrive successfully!');
      await loadFiles(); // Refresh file list
    } catch (error: any) {
      console.error('Error creating Excel file:', error);
      toast.error('Failed to create Excel file in OneDrive');
    } finally {
      setIsCreatingExcel(false);
    }
  };

  const runAIWorkflow = async () => {
    try {
      toast.info('Starting AI workflow with OneDrive + Excel data...');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase.functions.invoke('enhanced-data-refresh', {
        body: { 
          source: 'onedrive_excel',
          includeAIWorkflow: true
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success('AI workflow with OneDrive + Excel started successfully!');
    } catch (error: any) {
      console.error('Error running AI workflow:', error);
      toast.error('Failed to run AI workflow');
    }
  };

  const disconnect = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('microsoft_tokens')
        .delete()
        .eq('user_id', user.id);

      setIsConnected(false);
      setFiles([]);
      toast.success('Disconnected from OneDrive');
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect from OneDrive');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
              <Cloud className="w-4 h-4 text-white" />
            </div>
            <FileSpreadsheet className="w-5 h-5 text-green-700" />
            OneDrive + Excel Online
            {isConnected && (
              <Badge variant="default" className="bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Connected
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Connect OneDrive to automatically create and manage Excel files for analytics. The AI workflow will process data from Excel files stored in your OneDrive.
            </AlertDescription>
          </Alert>

          {!isConnected ? (
            <Button 
              onClick={handleOAuthConnect} 
              disabled={isConnecting}
              className="w-full"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting to OneDrive...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Connect with Microsoft OAuth
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                <Button onClick={loadFiles} disabled={isLoading} variant="outline">
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load Files'
                  )}
                </Button>
                <Button 
                  onClick={createExcelFile} 
                  disabled={isCreatingExcel} 
                  variant="outline"
                  className="bg-green-50 hover:bg-green-100"
                >
                  {isCreatingExcel ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Create Excel
                    </>
                  )}
                </Button>
                <Button onClick={runAIWorkflow} variant="outline">
                  Run AI Workflow
                </Button>
                <Button onClick={disconnect} variant="outline">
                  Disconnect
                </Button>
              </div>

              {files.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Excel Files in OneDrive</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {files.slice(0, 5).map((file) => (
                      <div key={file.id} className="p-3 border rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-green-700" />
                          <div className="font-medium">{file.name}</div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {file.size && `Size: ${(file.size / 1024).toFixed(1)} KB • `}
                          {file.lastModifiedDateTime && `Modified: ${new Date(file.lastModifiedDateTime).toLocaleDateString()}`}
                        </div>
                        {file.webUrl && (
                          <a 
                            href={file.webUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            Open in Excel Online
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {files.length === 0 && !isLoading && (
                <div className="text-center py-6">
                  <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">
                    No Excel files found. Create an Excel file to get started.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};